import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ProjectContext} from "../../contexts/ProjectContext";
import {CompanyType} from "../../models/company";
import {getBid, updateBid} from "../bid/bidding_api";
import {useToast} from "../ui/use-toast";
import {useNavigate, useParams} from "react-router-dom";
import {BidderSov, Cell, linesDecoder, linesRowMeta, RowMeta, sheetValues} from "../project_conf/bidConfig_model";
import {SubmitHandler} from "react-hook-form";
import {BidBaseModel, BidExtendedModel, BidStatus, BiddingView, Plug, PlugsView} from "../bid/bidding_model";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";
import {useAuthInfo} from "@propelauth/react";
import {FileView} from "../files/files";
import {BidFilesCard} from "../bid/BidFilesCard";
import {BidHeader} from "../bid/BidHeader";
import {BidConversation} from "../bid/BidConversation";
import {BidChecklist} from "../bid/BidChecklist";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {CopyButton2} from "../exportData/CopyButton";
import {copySheetDataToClipboard} from "../project_conf/datasheetHelpers";
import DataSheet from "../project_conf/DataSheet";
import CreateBidForm from "../bid/CreateBid";
import PasteFromExcel, { PasteDataToCellsBidder} from "../project_conf/PasteFromExcel";
import {BuilderFeedbackCard} from "../builder/AuctionStatusView";
import {SavePdfButton} from "../bid/SavePdfButton";
import {Button} from "../ui/button";
import {postWithToken} from "../../api/apiBase";
import {DeveloperBidCard} from "../bid/developer/DeveloperBidCard";
import {PlugAddButton, PlugCell, PlugPopover} from "../developer/PlugCell";
import {moneyFormatter} from "../../lib/formatters";
import {
    cellKey, devLineSum, hasAmount, indexPlugs, kindLabel, kindStyle, MARK_TAG, parseCellValue, setDeveloperCell,
    setDevLineLabel, signedMoney, STALE_PLUG, STALE_TITLE
} from "../developer/plugs";
import {SubRow} from "../project_conf/DataSheet";
import {SUGGESTION_LINE, SUGGESTION_TAG, useBidLeveling} from "../developer/ai_leveling";
import {SuggestionPopover} from "../developer/LevelingDetails";


type BidFormProps = {
    bid: BidBaseModel;
    isEditing: boolean;
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>> ;
    onSave: (data: BidBaseModel) => void;
    onSubmitBid: (data: BidBaseModel) => void;
    onAmountChange: (v: number) => void;
    aside: React.ReactNode;
}


type BidSheetProps = {
    sheetData: Cell[][] | null;
    canEdit: boolean;
    setSheetData:  React.Dispatch<React.SetStateAction<Cell[][] | null>> ;
    isEditing: boolean;
    // developer plugs - add button in the cell, plug lines under the row
    cellExtra?: (i: number, j: number, cell: Cell) => React.ReactNode;
    subRows?: (i: number) => SubRow[];
    // developer only lines (developers only ever get these) and saving the developer's cells on them
    rowMeta?: RowMeta[];
    onDeveloperCell?: (i: number, j: number, value: any) => void;
    onDeveloperLabel?: (i: number, label: string) => void;
}

/**
 * Competitive feedback - only present on the latest SUBMITTED bid.
 * Sits above the bid/files row so it does not push the bid form out of line.
 * */
function BidFeedback({feedback}: { feedback: BiddingView }) {
    return <Card className="border border-gray-200 bg-gray-50/50 shadow-none">
      <CardContent className="pt-4 pb-3 px-5 flex items-center gap-5">
        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400 shrink-0">
          Competitive<br />Position
        </div>

        <div className="flex-1">
          <div className="flex justify-center">
            <BuilderFeedbackCard bidViewData={feedback} showTitle={false} />
          </div>
          <p className="text-[10px] text-gray-400 italic mt-3 text-center leading-snug">
            Feedback is comparative and updates as bids are revised.
            Some fields may rank only among bidders who provided that item.
          </p>
        </div>
      </CardContent>
    </Card>
}


function BidForm(
    {bid,
        isEditing,
        setIsEditing,
        onSubmitBid,
        onSave,
        onAmountChange,
        aside
    }: BidFormProps) {

    return <CreateBidForm
                editable={bid.status !== BidStatus.Submitted}
                bid={bid}
                create={false}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                onSubmit={onSubmitBid}
                onSave={onSave}
                onAmountChange={onAmountChange}
                aside={aside}
            />
}


/**
 * The bidder-filled datasheet. Used to be called "Additional Info".
 * */
function BidSovCard({sheetData, canEdit, setSheetData, isEditing, cellExtra, subRows,
                        rowMeta, onDeveloperCell, onDeveloperLabel}: BidSheetProps) {

    const importFromExcel = (data: any[]) => {
        const new_data = PasteDataToCellsBidder(sheetData, data);
        setSheetData(new_data);
    }

    if (!sheetData) {
        return <></>;
    }

    return <Card>
            <CardHeader>
                <CardTitle className="columns-2 flex justify-between">
                    <div>Schedule of Values</div>
                    <div className=" flex gap-2">
                        <CopyButton2
                            dataTransform={() => sheetData}
                            executeCustomCopy={copySheetDataToClipboard}
                        />
                        {canEdit &&
                            <PasteFromExcel pasteDataFromExcel={importFromExcel}/>
                        }
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <DataSheet
                    data={sheetData}
                    setData={setSheetData}
                    enable_row_col={false}
                    // readonly={!canEdit}
                    readonly={!isEditing}
                    cellExtra={cellExtra}
                    subRows={subRows}
                    rowMeta={rowMeta}
                    onDeveloperCell={onDeveloperCell}
                    onDeveloperLabel={onDeveloperLabel}
                />
            </CardContent>
        </Card>
}


export function BidPage() {
    // Builders not allowed to view this
    const {accessToken} = useAuthInfo();
    const projectUserContext = useContext(ProjectContext);
    const {toast} = useToast();
    const navigate = useNavigate();
    const {project_id, bid_id} = useParams<string>();
    const [bid, setBid] = useState<BidExtendedModel | null>(null);
    const [sheetData, setSheetData] = React.useState<Cell[][] | null>(null);
    const [isEditing, setIsEditing] = React.useState<boolean>(true);
    // these two only drive the checklist - nothing is saved from them.
    const [fileCount, setFileCount] = useState<number>(0);
    const [liveAmount, setLiveAmount] = useState<number>(0);
    const filesRef = useRef<HTMLDivElement>(null);
    // developer leveling - every plug write returns the bid's plugs + leveled amount
    const [plugs, setPlugs] = useState<Plug[]>([]);
    const [leveled, setLeveled] = useState<number | null>(null);
    const plugIdx = useMemo(() => indexPlugs(plugs), [plugs]);
    // SOV cell id -> [row, col] in the grid - plugs and AI suggestions sit on a cell id
    const cellPos = useMemo(() => {
        const out: Record<number, [number, number]> = {};
        sheetData?.forEach((row, i) => row.forEach((cell, j) => {
            if (cell.id != null) out[cell.id] = [i, j];
        }));
        return out;
    }, [sheetData]);
    // developer only lines - builders never get them, so for a builder this is all normal lines
    const [rowMeta, setRowMeta] = useState<RowMeta[]>([]);
    // AI leveling against the other bids on the scope - the developer card and the SOV's suggestion lines share it
    const leveling = useBidLeveling(project_id as string, bid_id as string,
        projectUserContext?.role === CompanyType.developer && bid?.status === BidStatus.Submitted);

    const load = () => {
        getBid(project_id as string, bid_id as string, accessToken as string)
            .then((data) => {
                    // console.log(data)
                    setValues(data);
                }
            );
    }

    useEffect(() => {
        setBid(null);
        setSheetData(null);
        load();
    }, [project_id, bid_id]);

    const canEdit =
        projectUserContext?.role === CompanyType.builder &&
        bid?.status !== BidStatus.Submitted;

    const setValues = (data: any) => {
        setBid(data);
        setLiveAmount(data.bid_amount);
        setPlugs(data.developer?.plugs ?? []);
        setLeveled(data.developer?.leveled_amount ?? null);
        // the bid's SOV with its values - rendered for the viewer by the server
        if (data.sov_lines) {
            setSheetData(linesDecoder(data.sov_lines));
            setRowMeta(linesRowMeta(data.sov_lines));
        }
    }

    const onPlugsChanged = (view: PlugsView) => {
        setPlugs(view.plugs);
        setLeveled(view.leveled_amount);
    }

    // the developer typed in a developer only cell - the grid already shows it, save it and re-level
    const onDeveloperCell = (i: number, j: number, value: any) => {
        // text lines keep what was typed, the others take a number
        const text = sheetData?.[i]?.[j]?.type === "txt";
        const v = text ? (String(value ?? "").trim() || null) : parseCellValue(value);
        setDeveloperCell(project_id as string, bid_id as string, sheetData?.[i]?.[j]?.id as number, v,
            accessToken as string)
            .then(onPlugsChanged)
            .catch((err) => {
                toast({title: "Error", description: "Not saved: " + err.message, variant: "destructive"});
            });
    }

    // the developer renamed a developer only line - the grid already shows it. it's a template change
    const onDeveloperLabel = (i: number, label: string) => {
        // the line's name is its first cell
        setDevLineLabel(project_id as string, bid?.sov?.config_id as string, sheetData?.[i]?.[0]?.id as number, label,
            accessToken as string)
            .catch((err) => {
                toast({title: "Error", description: "Line not renamed: " + err.message, variant: "destructive"});
            });
    }

    const onFilesLoaded = (files: FileView[]) => {
        setFileCount(files.filter((f) => !f.is_folder).length);
    }

    const onGoToFiles = () => {
        filesRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
    }

    // the bidder's values by cell id on the bid's SOV - save, submit and copy send it
    const sovToSend = (): BidderSov | null =>
        bid?.sov && sheetData ? {config_id: bid.sov.config_id, values: sheetValues(sheetData)} : null;

    const onSave: SubmitHandler<BidBaseModel> = (data: BidBaseModel) => {
        if (data.bid_amount < 0) {
            toast({
                title: "Error",
                description: "Bid amount must be greater than 0.",
                variant: "destructive",
            });
            return;
        }
        if (!bid_id) {
            return;
        }

        const submit_data = {...data, sov: sovToSend()};
        updateBid(accessToken as string, project_id as string, bid_id, submit_data)
            .then((data) => {
                toast({
                    title: "Success!",
                    description: "Update Successful",
                    variant: "default",
                });
                setIsEditing(false);
            })
            .catch((error) => {
                console.error("Error updating bid:", error);
                toast({
                    title: "Error",
                    description: "Failed to update bid: " + JSON.stringify(error),
                    variant: "destructive",
                });
            });

    };

    const onSubmitBid = (values: BidBaseModel) => {
        if (!bid_id) {
            return;
        }
        values.status = BidStatus.Submitted;
        values.sov = sovToSend();
        updateBid(accessToken as string, project_id as string, bid_id, values)
            .then((data) => {
                toast({
                    title: "Success!",
                    description: "Bid submitted successfully.",
                    variant: "default",
                });

                setIsEditing(false);
                navigate(0);
            })
            .catch((error) => {
                console.error("Error submitting bid:", error);
                toast({
                    title: "Error",
                    description: JSON.stringify(error) || "Failed to submit bid.",
                    variant: "destructive",
                });
            });
        console.log("values", values);

    };

    const onCopyBid = () => {
        if(!bid) return;

        const url = bid.scope_id
          ? `/projects/${project_id}/scope/${bid.scope_id}/bidding/create`
          : `/projects/${project_id}/bidding/create`;


        const copyData = {
            title: bid.title,
            bid_amount: 0,
            description: bid.description,
            inclusions: bid.inclusions,
            exclusions: bid.exclusions,
            status: BidStatus.Draft,
            sov: sovToSend()
        };
        postWithToken(url, JSON.stringify(copyData), accessToken as string)
          .then((new_data) => {
            navigate(`/project/${project_id}/bid/${new_data.id}`);
          })
          .catch((error) => {
            console.error("Error copying bid:", error);
            toast({
              title: "Error",
              description: "Failed to copy bid: " + JSON.stringify(error),
              variant: "destructive",
            });
          });
      };
    if (!bid) {
        return <div><BaseLoadingSpinner/></div>;
    }

    // the checklist is guidance for a bidder who can still act on it.
    const showChecklist = canEdit === true;
    const showRail = showChecklist || bid.status === BidStatus.Submitted;
    // developer card has the download button + AI review
    const showDeveloperCard = projectUserContext?.role === CompanyType.developer &&
        bid.status === BidStatus.Submitted;

    // the plug popover for one SOV cell. convert to line changes the SOV, so the bid is reloaded
    const plugPopover = (i: number, j: number, trigger: React.ReactElement) => {
        const sub = parseFloat(String(sheetData?.[i]?.[j]?.value));
        return <PlugPopover
            project_id={project_id as string}
            bid_id={bid_id as string}
            cell_id={sheetData?.[i]?.[j]?.id as number}
            plugs={plugIdx[cellKey(sheetData?.[i]?.[j]?.id)] || []}
            subValue={isNaN(sub) ? null : sub}
            onChange={onPlugsChanged}
            onConverted={load}
            infoOnly={sheetData?.[i]?.[j]?.type === "txt"}>
            {trigger}
        </PlugPopover>
    }

    // "+" in the bidder's money / number / text inputs - text only takes INFO plugs.
    // not on developer only lines - the amount is typed in there
    const plugExtra = (i: number, j: number, cell: Cell) => {
        if (!cell.party || cell.type === "date") return null;
        if (rowMeta[i] && rowMeta[i].developer_only) return null;
        // included / excluded stay in their cell - a tag, not a line under the row. note on hover
        const marks = (plugIdx[cellKey(cell.id)] || []).filter((p) => !hasAmount(p.kind));
        return <>
            {marks.map((p) => <React.Fragment key={p.id}>
                {plugPopover(i, j,
                    <button type="button" title={[p.stale ? STALE_TITLE : "", p.note].filter(Boolean).join(". ") || undefined}
                            className={MARK_TAG + " " + kindStyle(p.kind).tag + (p.stale ? " " + STALE_PLUG : "")}>
                        {kindLabel(p.kind)}
                    </button>)}
            </React.Fragment>)}
            {plugPopover(i, j, <PlugAddButton/>)}
        </>;
    }

    // a line under row i for each of its plugs: note in the first column, amount in the plug's column
    const plugLines = (i: number): SubRow[] => plugs
        // included / excluded stay in their cell (plugExtra) - only plugs with an amount get a line
        .filter((p) => p.cell_id != null && cellPos[p.cell_id]?.[0] === i && hasAmount(p.kind))
        .map((p) => {
            const j = cellPos[p.cell_id as number][1];
            const style = kindStyle(p.kind);
            const label = <span className="flex gap-1 items-center" title={p.stale ? STALE_TITLE : undefined}>
                <span className={"rounded px-1 text-[10px] font-medium " + style.tag}>
                    {kindLabel(p.kind)}
                </span>
                {p.note || <i className="text-gray-400">no note</i>}
            </span>;
            const amount = plugPopover(i, j,
                <button type="button" className="w-full text-right hover:underline">
                    {signedMoney(p.amount)}
                </button>);
            return {
                key: p.id,
                cells: j === 0 ? {0: <>{label}{amount}</>} : {0: label, [j]: amount},
                className: style.line + (p.stale ? " " + STALE_PLUG : "")
            };
        });

    // a dashed line under row i for each pending AI leveling suggestion - accept / reject in its popover
    const suggestionLines = (i: number): SubRow[] => leveling.pending
        .filter((s) => s.cell_id != null && cellPos[s.cell_id]?.[0] === i)
        .map((s) => {
            const j = cellPos[s.cell_id as number][1];
            const label = <span className="flex gap-1 items-center">
                <span className={"rounded px-1 text-[10px] font-medium " + SUGGESTION_TAG}>
                    AI {kindLabel(s.kind)}
                </span>
                {s.note}
            </span>;
            const amount = <SuggestionPopover
                suggestion={s}
                bidders={leveling.doc?.bids ?? []}
                onAccept={(edits) => leveling.accept(s, edits).then(onPlugsChanged)}
                onReject={() => leveling.reject(s)}>
                <button type="button" className="w-full text-right hover:underline">
                    {signedMoney(s.amount)}
                </button>
            </SuggestionPopover>;
            return {
                key: s.id,
                cells: j === 0 ? {0: <>{label}{amount}</>} : {0: label, [j]: amount},
                className: SUGGESTION_LINE
            };
        });

    // the AI review popup's other side - the same SOV, plugs and suggestions as the page (same state)
    const reviewSov = <>
        <div className="flex items-center gap-2 text-sm mb-2">
            <span className="text-gray-500">Bid {moneyFormatter.format(bid.bid_amount)}</span>
            <span>· Leveled <span className="font-medium">{moneyFormatter.format(leveled ?? bid.bid_amount)}</span></span>
            {/* base bid plugs - leveling that isn't on an SOV line */}
            <PlugCell
                project_id={project_id as string}
                bid_id={bid_id as string}
                cell_id={null}
                plugs={plugs.filter((p) => p.cell_id == null)}
                subValue={bid.bid_amount}
                onChange={onPlugsChanged}/>
        </div>
        <BidSovCard
            sheetData={sheetData}
            setSheetData={setSheetData}
            canEdit={canEdit}
            isEditing={false}
            cellExtra={plugExtra}
            subRows={(i) => [...plugLines(i), ...suggestionLines(i)]}
            rowMeta={rowMeta}
            onDeveloperCell={onDeveloperCell}
            onDeveloperLabel={onDeveloperLabel}/>
    </>;

    return (
        <div className="flex justify-between">
            <div className={showRail ? "w-8/12 mr-4" : "w-full"}>
                <BidHeader key={bid_id} bid={bid}/>

                <div className="flex justify-end gap-2 mb-2">
                    {!showDeveloperCard && <SavePdfButton bid={bid} sheetData={sheetData}/>}
                    {projectUserContext?.role === CompanyType.builder &&
                        <Button variant="outline" type="button" onClick={onCopyBid}>
                          Copy & Revise
                        </Button>
                    }
                </div>

                {bid.feedback &&
                    <>
                        <BidFeedback feedback={bid.feedback}/>
                        <br/>
                    </>
                }

                {showDeveloperCard &&
                    <>
                        <DeveloperBidCard key={bid_id} bid={bid} sheetData={sheetData}
                                          plugs={plugs} leveled={leveled} onPlugsChanged={onPlugsChanged}
                                          devLines={sheetData ? devLineSum(sheetData, rowMeta) : 0}
                                          leveling={leveling}
                                          reviewSov={reviewSov}/>
                        <br/>
                    </>
                }


                <div ref={filesRef}>
                    <BidForm
                        key={bid_id}
                        bid={bid}
                        onSubmitBid={onSubmitBid}
                        onSave={onSave}
                        isEditing={canEdit === true ? isEditing : false}
                        setIsEditing={setIsEditing}
                        onAmountChange={setLiveAmount}
                        aside={
                            <BidFilesCard
                                key={bid_id}
                                folder_id={bid.root?.id}
                                canEdit={canEdit === true}
                                onFilesLoaded={onFilesLoaded}/>
                        }/>
                </div>
                <br/>


                <BidSovCard
                    sheetData={sheetData}
                    setSheetData={setSheetData}
                    canEdit={canEdit}
                    isEditing={canEdit === true ? isEditing : false}
                    cellExtra={showDeveloperCard ? plugExtra : undefined}
                    subRows={showDeveloperCard ? (i) => [...plugLines(i), ...suggestionLines(i)] : undefined}
                    rowMeta={rowMeta}
                    onDeveloperCell={showDeveloperCard ? onDeveloperCell : undefined}
                    onDeveloperLabel={showDeveloperCard ? onDeveloperLabel : undefined}/>
            </div>

            {showChecklist &&
                <div className="w-4/12 sticky top-20 self-start">
                    <BidChecklist
                        bidAmount={liveAmount}
                        fileCount={fileCount}
                        sheetData={sheetData}
                        onGoToFiles={onGoToFiles}/>
                </div>
            }
            {!showChecklist && bid.status === BidStatus.Submitted &&
                <div className="w-4/12 ">
                    <BidConversation key={bid_id}  parent_type={"bidding"} parent_id={bid.id}/>
                </div>
            }
        </div>)

}
